import { FC, useCallback } from "react";
import { useMemo } from "react";
import React from "react";
import { FormRenderProps, useForm, useFormState } from "react-final-form";
import { Field } from "react-final-form";
import Typography from "@material-ui/core/Typography";
import Loading from "../../../../components/Loading";
import { Grid } from "@material-ui/core";
import BootstrapInput from "../../../../components/BootstrapInput";
import DatePicker from "../../../../components/DatePicker";
import Autocomplete from "../../../../components/Autocomplete";
import PasswordInput from "../../../../components/PasswordInput";
import Hidden from "@material-ui/core/Hidden";
import Divider from "@material-ui/core/Divider";
import Button from "../../../../components/Button";
import { getData } from "country-list";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { Theme } from "@material-ui/core/styles";
import TermsApproval from "../TermsApproval";
import UploadFile from "../../../../components/UploadFile/UploadFile";
import { GENDER } from "../../../user/types";

const useStyles = makeStyles((theme: Theme) => ({
  fieldsContainer: {
    marginBottom: -4,
    "& .MuiGrid-item": {
      padding: 8,
    },
  },
  divider: {
    marginTop: 12,
    marginBottom: 24,
  },
  recaptchaText: {
    marginTop: 8,
  },
}));

const RegisterStudentForm: FC<FormRenderProps> = ({
  handleSubmit,
  submitError,
  submitting,
}) => {
  const classes = useStyles();
  const { values } = useFormState();
  const { change } = useForm();

  const countries = useMemo(
    () =>
      getData().sort((a, b) =>
        a.name > b.name ? 1 : b.name > a.name ? -1 : 0,
      ),
    [],
  );

  const files = useMemo(
    () => (values.image ? [values.image] : []),
    [values.image],
  );

  const addNewFiles = useCallback(
    async (files) => {
      const file = files[0];

      change("image", {
        id: 1,
        name: file.name,
        size: file.size,
        file,
      });
    },
    [change],
  );

  const removeFileHandler = useCallback(() => {
    change("image", null);
  }, [change]);

  return (
    <form noValidate onSubmit={handleSubmit}>
      {submitError && <Typography color="error">{submitError}</Typography>}
      {submitting && <Loading />}

      <Grid container spacing={3} className={classes.fieldsContainer}>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="name"
            label="Full Name"
            placeholder="John Smith"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="phone"
            label="Phone Number"
            placeholder="+19159969739"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            autoOk
            fullWidth
            name="studentProfile.birth_date"
            label="Birth Date"
            component={DatePicker}
            placeholder="25.09.1992"
            animateYearScrolling
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            name="studentProfile.gender"
            label="Gender"
            options={GENDER}
            placeholder="Please Select"
            component={Autocomplete}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            name="studentProfile.country"
            label="Country"
            options={countries}
            placeholder="Please Select"
            component={Autocomplete}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="studentProfile.city"
            label="City"
            placeholder="Claymont"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            name="studentProfile.citizenship"
            label="Citizenship"
            options={countries}
            placeholder="Please Select"
            component={Autocomplete}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="email"
            label="Email"
            placeholder="example@test.com"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            name="password"
            label="Password"
            placeholder="Must have at least 8 characters"
            autoComplete="new-password"
            component={PasswordInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            label="Repeat Password"
            placeholder="Must have at least 8 characters"
            name="password_confirmation"
            component={PasswordInput}
          />
        </Grid>
      </Grid>

      <Hidden smDown>
        <Divider className={classes.divider} />
      </Hidden>
      <Grid container spacing={3} alignItems={"center"} justify={"flex-end"}>
        <Grid item sm={4} xs={12}>
          <UploadFile
            uploadedFiles={files}
            addNewFiles={addNewFiles}
            removeFile={removeFileHandler}
            titleText={"Photo"}
            subText={"Drag & Drop Your image"}
            allowedFileTypes={["jpg", "jpeg", "png"]}
            maxSize={5}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} alignItems={"center"} justify={"flex-end"}>
        <Grid item sm={4} xs={12}>
          <TermsApproval termsText={""} />
          <Button
            fullWidth
            type="submit"
            color="secondary"
            variant="contained"
            disabled={submitting}
          >
            Sign Up
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={3} alignItems={"center"} justify={"flex-end"}>
        <Grid item sm={4} xs={12}>
          <Typography
            component="p"
            align="center"
            className={classes.recaptchaText}
          >
            This site is protected by reCAPTCHA and the Google{" "}
            <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
            <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
            apply.
          </Typography>
        </Grid>
      </Grid>
    </form>
  );
};

export default RegisterStudentForm;
